import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useHistory, useParams } from 'react-router-dom';
import { getSearchDateParamFromMoment } from 'utils/search/searchDateParam';
import DayButton from 'ui/atoms/DayButton';
import useShowDaysCarousel from 'components/search/DaysCarousel/useShowDaysCarousel';
import 'ui/molecules/DayControls';

/**
 * DayControls component.
 * @param {object} props - way, fixed, prevDayDate, nextDayDate, prevDayPrice, nextDayPrice, disabledButtons, originId, destinationId, departureDate, returnDate, isRoundTrip, isProviderList, providerId, encodedPassengersSelection.
 * @param {string} props.way - Trip direction: 'departure' or 'return'.
 * @param {boolean} props.fixed - Whether the control is fixed position.
 * @param {object} props.prevDayDate - Moment object for the previous day.
 * @param {object} props.nextDayDate - Moment object for the next day.
 * @param {string} props.prevDayPrice - Formatted price for the previous day.
 * @param {string} props.nextDayPrice - Formatted price for the next day.
 * @param {object} props.disabledButtons - Object with prevDay/nextDay disabled states.
 * @param {string} props.originId - Origin city slug.
 * @param {string} props.destinationId - Destination city slug.
 * @param {string} props.departureDate - Departure date param string.
 * @param {string} props.returnDate - Return date param string.
 * @param {boolean} props.isRoundTrip - Whether the trip is round trip.
 * @param {boolean} props.isProviderList - Whether shown inside a provider list.
 * @param {string} props.providerId - Provider identifier.
 * @param {string} props.encodedPassengersSelection - Encoded passengers string.
 */
const DayControls = ({
  way,
  fixed,
  prevDayDate,
  nextDayDate,
  prevDayPrice,
  nextDayPrice,
  disabledButtons,
  originId,
  destinationId,
  departureDate,
  returnDate,
  isRoundTrip,
  isProviderList,
  providerId,
  encodedPassengersSelection = 'A1',
}) => {
  const showDaysCarousel = useShowDaysCarousel();
  const { departureId } = useParams();
  const history = useHistory();

  if (showDaysCarousel) return null;

  const prevDayDateDisplay = prevDayDate.format('DD MMM');
  const nextDayDateDisplay = nextDayDate.format('DD MMM');
  const isOpen = window.location.href.split('/').pop().includes('open');

  /**
   * Format a date value into the search param format.
   * @param {string|object} date - Date string or moment object.
   * @returns {string} Formatted date string.
   */
  const formatActionDate = (date) => {
    const momentDate = moment.isMoment(date) ? date : moment(date, 'DD-MM-YYYY');
    return getSearchDateParamFromMoment(momentDate);
  };

  /**
   * Build a round-trip search URL segment.
   * @param {string} url - Base URL prefix.
   * @param {string|object} returns - Return date.
   * @param {string} button - Button action identifier ('prev' | 'next').
   * @returns {string} Complete round-trip URL.
   */
  const createRoundTrip = (url, returns, button) => {
    const formatedReturns = formatActionDate(returns);
    let newUrl = url;
    newUrl += `${formatedReturns}/`;

    const finalPath = providerId ? `/${providerId}?action=${button}` : `?action=${button}`;
    newUrl += `p/${encodedPassengersSelection}/`;
    const returnsFinalPath = `${departureId}/returns`;

    if (isProviderList) {
      if (way === 'return') {
        newUrl += `${departureId}/return/`;
      }
      newUrl += 'providers';
      newUrl += isOpen ? '/open' : finalPath;

      return newUrl;
    }

    newUrl += way === 'return' ? returnsFinalPath : 'departures';
    newUrl += isOpen ? '/open' : finalPath;

    return newUrl;
  };

  /**
   * Build a one-way departure search URL segment.
   * @param {string} url - Base URL prefix.
   * @param {string} button - Button action identifier ('prev' | 'next').
   * @returns {string} Complete one-way URL.
   */
  const createDepartureTrip = (url, button) => {
    let newUrl = url;

    const finalPath = providerId ? `/${providerId}?action=${button}` : `?action=${button}`;
    newUrl += isProviderList
      ? `p/${encodedPassengersSelection}/providers`
      : `p/${encodedPassengersSelection}/departures`;
    newUrl += isOpen ? '/open' : finalPath;

    return newUrl;
  };

  /**
   * Build the full search URL for a given date and button action.
   * @param {string|object} date - The target date.
   * @param {string} button - Button action identifier ('prev' | 'next').
   * @returns {string} Full search URL.
   */
  const createUrl = (date, button) => {
    const departure = way === 'departure' ? date : departureDate;
    const formatedDeparture = formatActionDate(departure);
    const returns = way === 'return' ? date : returnDate;

    let url = `/search/${originId}/${destinationId}/${formatedDeparture}/`;
    url = isRoundTrip ? createRoundTrip(url, returns, button) : createDepartureTrip(url, button);
    return url;
  };

  /**
   * Return a click handler that navigates to the search URL for a given date.
   * @param {string|object} date - The target date.
   * @param {string} button - Button action identifier ('prev' | 'next').
   * @returns {Function} Click handler.
   */
  const handleOptionSelect = (date, button) => () => {
    const url = createUrl(date, button);
    history.push(url);
  };

  return (
    <div className={`day-controls ${fixed ? 'day-controls-fixed' : ''}`}>
      <DayButton
        onClick={handleOptionSelect(prevDayDate, 'prev')}
        date={prevDayDateDisplay}
        price={prevDayPrice}
        leftIcon
        disabled={disabledButtons.prevDay}
      />
      <DayButton
        onClick={handleOptionSelect(nextDayDate, 'next')}
        date={nextDayDateDisplay}
        price={nextDayPrice}
        rightIcon
        disabled={disabledButtons.nextDay}
      />
    </div>
  );
};

DayControls.propTypes = {
  way: PropTypes.string.isRequired,
  fixed: PropTypes.bool,
  prevDayDate: PropTypes.object.isRequired,
  nextDayDate: PropTypes.object.isRequired,
  prevDayPrice: PropTypes.string.isRequired,
  nextDayPrice: PropTypes.string.isRequired,
  disabledButtons: PropTypes.object.isRequired,
  originId: PropTypes.string.isRequired,
  destinationId: PropTypes.string.isRequired,
  departureDate: PropTypes.string.isRequired,
  returnDate: PropTypes.string.isRequired,
  isRoundTrip: PropTypes.bool.isRequired,
  isProviderList: PropTypes.bool,
  providerId: PropTypes.string,
  encodedPassengersSelection: PropTypes.string.isRequired,
};

DayControls.defaultProps = {
  fixed: false,
};

export default DayControls;
